import React, { useState, useEffect, useMemo } from 'react';
import {
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Box,
    Autocomplete,
    Typography
} from '@mui/material';
import { Edit as EditIcon, AutoAwesome as IAIcon } from '@mui/icons-material';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import api from '../../../../services/api';

// Helper de formatação de números (PT-BR)
const formatNumber = (value) => {
    if (value === undefined || value === null || value === '') return '';
    return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(value);
};

// Helper de parser (1.000,00 -> 1000.00)
const parseNumber = (value, isFloat = false) => {
    if (!value) return 0;
    // Remove pontos de milhar e troca vírgula por ponto
    const clean = value.toString().replace(/\./g, '').replace(',', '.');
    return isFloat ? parseFloat(clean) : parseInt(clean);
};

// Lógica de cálculo de bônus para sites pequenos
// Sincronizada com backend para consistência
const calcularBonusPequenos = (visitantesDia) => {
    if (!visitantesDia || visitantesDia <= 0) return 1.0;
    if (visitantesDia >= 10000) return 1.0;

    // Bônus Suave: 5 - log10(visitantes)
    // 10 visitas -> 5 - 1 = 4x
    // 100 visitas -> 5 - 2 = 3x
    // 1.000 visitas -> 5 - 3 = 2x
    // 10.000 visitas -> 5 - 4 = 1x
    const logVisitantes = Math.log10(visitantesDia);
    let bonus = 5 - logVisitantes;

    return Math.max(1.0, Math.min(4.0, bonus));
};

// Lógica de cálculo de CPC - Fórmula logarítmica (sincronizada com backend)
// Recebe audiência DIÁRIA e retorna CPC
const calculateCPC = (visitantesDia) => {
    if (!visitantesDia || visitantesDia <= 0) return 0;

    let cpcBase;

    // Para mega-portais (>1M visitas/dia), aplica compressão logarítmica adicional
    if (visitantesDia > 1000000) {
        const BASE = 154.3;
        const ESCALA = 2.968;
        const cpcBase1M = BASE / Math.pow(Math.log10(1000010), ESCALA);
        const val1M = cpcBase1M * 1000000;

        const fatorMultiplicador = visitantesDia / 1000000;
        const compressao = 1 + (Math.log10(fatorMultiplicador) * 0.5);

        const valoracao = val1M * compressao;
        cpcBase = valoracao / visitantesDia;
    } else {
        // Para sites menores (<1M), usa fórmula logarítmica normal
        const BASE = 154.3;
        const ESCALA = 2.968;

        cpcBase = BASE / Math.pow(Math.log10(visitantesDia + 10), ESCALA);
    }

    // Aplicar bônus para sites pequenos
    const bonus = calcularBonusPequenos(visitantesDia);
    const cpcComBonus = cpcBase * bonus;

    // Limites de CPC
    return Math.max(0.01, Math.min(400, cpcComBonus));
};

// Função para recalcular valores derivados
const recalculateValues = (monthly, ppvVal) => {
    const monthlyNum = parseInt(monthly) || 0;
    const ppvNum = parseFloat(ppvVal) || 0;

    if (monthlyNum > 0) {
        const daily = Math.round(monthlyNum / (365 / 12));
        const pvMonthly = Math.round(monthlyNum * ppvNum);
        const pvDaily = Math.round(daily * ppvNum);

        // CPC é calculado com base na audiência DIÁRIA
        const cpc = calculateCPC(daily);
        // Valoração = CPC × audiência DIÁRIA
        const valuation = Math.round(cpc * daily);

        let tier = 3;
        if (daily >= 100000) tier = 1;
        else if (daily >= 10000) tier = 2;

        return {
            audience: daily, // Daily
            audienceDaily: daily,
            audienceMonthly: monthlyNum,
            pageViews: pvDaily,
            pageViewsMonthly: pvMonthly,
            valuation: valuation,
            cpc: cpc, // Incluir CPC para exibição
            tier: tier
        };
    }
    return {};
};

const ufMapping = {
    'AC': 'Acre', 'AL': 'Alagoas', 'AP': 'Amapá', 'AM': 'Amazonas', 'BA': 'Bahia',
    'CE': 'Ceará', 'DF': 'Distrito Federal', 'ES': 'Espírito Santo', 'GO': 'Goiás',
    'MA': 'Maranhão', 'MT': 'Mato Grosso', 'MS': 'Mato Grosso do Sul', 'MG': 'Minas Gerais',
    'PA': 'Pará', 'PB': 'Paraíba', 'PR': 'Paraná', 'PE': 'Pernambuco', 'PI': 'Piauí',
    'RJ': 'Rio de Janeiro', 'RN': 'Rio Grande do Norte', 'RS': 'Rio Grande do Sul',
    'RO': 'Rondônia', 'RR': 'Roraima', 'SC': 'Santa Catarina', 'SP': 'São Paulo',
    'SE': 'Sergipe', 'TO': 'Tocantins'
};

// Mapeamento inverso para inicialização (Estado -> UF)
const estadoToUf = Object.entries(ufMapping).reduce((acc, [uf, estado]) => {
    acc[estado.toLowerCase()] = uf;
    return acc;
}, {});

const normalizeKey = (value) =>
    String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\w]/g, '')
        .toLowerCase();

const extractCategoryText = (item) => {
    if (typeof item === 'string') return item;
    if (!item) return '';
    return item.value || item.label || item.name || '';
};

const tipos = ['Site', 'Portal', 'Blog', 'Rede Social', 'Governo', 'Jornal', 'Revista'];
const abrangencias = ['Nacional', 'Regional', 'Local'];
const paises = [
    // Lusófonos
    { code: 'BR', name: 'Brasil' },
    { code: 'PT', name: 'Portugal' },
    { code: 'AO', name: 'Angola' },
    { code: 'MZ', name: 'Moçambique' },
    { code: 'CV', name: 'Cabo Verde' },
    { code: 'GW', name: 'Guiné-Bissau' },
    { code: 'ST', name: 'São Tomé e Príncipe' },
    { code: 'TL', name: 'Timor-Leste' },
    { code: 'GQ', name: 'Guiné Equatorial' },
    // América do Norte
    { code: 'US', name: 'Estados Unidos' },
    { code: 'CA', name: 'Canadá' },
    { code: 'MX', name: 'México' },
    // América do Sul
    { code: 'AR', name: 'Argentina' },
    { code: 'CL', name: 'Chile' },
    { code: 'CO', name: 'Colômbia' },
    { code: 'PE', name: 'Peru' },
    { code: 'VE', name: 'Venezuela' },
    { code: 'UY', name: 'Uruguai' },
    { code: 'PY', name: 'Paraguai' },
    { code: 'BO', name: 'Bolívia' },
    { code: 'EC', name: 'Equador' },
    // Europa Ocidental
    { code: 'ES', name: 'Espanha' },
    { code: 'FR', name: 'França' },
    { code: 'DE', name: 'Alemanha' },
    { code: 'IT', name: 'Itália' },
    { code: 'GB', name: 'Reino Unido' },
    { code: 'NL', name: 'Holanda' },
    { code: 'BE', name: 'Bélgica' },
    { code: 'CH', name: 'Suíça' },
    { code: 'AT', name: 'Áustria' },
    { code: 'SE', name: 'Suécia' },
    { code: 'NO', name: 'Noruega' },
    { code: 'DK', name: 'Dinamarca' },
    { code: 'FI', name: 'Finlândia' },
    { code: 'IE', name: 'Irlanda' },
    // Europa Oriental
    { code: 'PL', name: 'Polônia' },
    { code: 'RU', name: 'Rússia' },
    { code: 'UA', name: 'Ucrânia' },
    { code: 'CZ', name: 'República Tcheca' },
    { code: 'RO', name: 'Romênia' },
    // Ásia
    { code: 'CN', name: 'China' },
    { code: 'JP', name: 'Japão' },
    { code: 'KR', name: 'Coreia do Sul' },
    { code: 'IN', name: 'Índia' },
    { code: 'ID', name: 'Indonésia' },
    { code: 'TH', name: 'Tailândia' },
    { code: 'VN', name: 'Vietnã' },
    { code: 'SG', name: 'Singapura' },
    { code: 'MY', name: 'Malásia' },
    { code: 'PH', name: 'Filipinas' },
    // Oceania
    { code: 'AU', name: 'Austrália' },
    { code: 'NZ', name: 'Nova Zelândia' },
    // África
    { code: 'ZA', name: 'África do Sul' },
    { code: 'EG', name: 'Egito' },
    { code: 'NG', name: 'Nigéria' },
    { code: 'KE', name: 'Quênia' }
];

const DomainEditDialog = ({ open, onClose, domain, onSave, isNew = false }) => {
    const [availableCategories, setAvailableCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [formData, setFormData] = useState({
        domain: '',
        siteName: '',
        pais: 'BR',
        sede: '',
        estado: '',
        cidade: '',
        uf: '',
        tipo: 'Site',
        abrangencia: '',
        categories: [],
        audience: 0,
        audienceMonthly: 0,
        audienceDaily: 0,
        subscribers: 0,
        pageViews: 0,
        pageViewsMonthly: 0,
        ppv: 2.5,
        valuation: 0,
        tier: 3,
        description: '',
        updateSource: 'automatic'
    });

    // Estado separado para texto do PPV (permite digitação com vírgula)
    const [ppvText, setPpvText] = useState('2,5');

    const loadCategories = async () => {
        if (availableCategories.length > 0) return;

        try {
            setLoadingCategories(true);
            const res = await api.get('/categories');
            if (res.data.success) {
                setAvailableCategories(res.data.data || []);
            }
        } catch (err) {
            console.error('Erro ao carregar categorias:', err);
        } finally {
            setLoadingCategories(false);
        }
    };

    useEffect(() => {
        if (open) {
            loadCategories();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (domain) {
            console.log('[DomainEditDialog] Domain data received:', {
                _id: domain._id,
                domain: domain.domain,
                siteName: domain.siteName,
                enrichedAt: domain.enrichedAt,
                valuation: domain.valuation,
                cpc: domain.cpc
            });

            // Mapear campos do domínio para o formulário
            // PendingDomains pode ter campos diferentes (estimatedAudience) que precisamos normalizar
            const audienceMonthly = domain.audienceMonthly || domain.estimatedAudience || 0;
            const ppv = domain.ppv || domain.estimatedPPV || 2.5;

            // Inicializar texto do PPV com formato brasileiro (vírgula)
            setPpvText(ppv.toString().replace('.', ','));

            // Tentar encontrar UF se tiver apenas o nome do estado
            let initialUf = domain.uf || '';
            if (!initialUf && domain.estado && domain.pais === 'BR') {
                initialUf = estadoToUf[domain.estado.toLowerCase()] || '';
            }

            // USAR VALORES DO BACKEND DIRETAMENTE - NÃO RECALCULAR!
            const rawCategories = Array.isArray(domain.categories) && domain.categories.length > 0
                ? domain.categories
                : (Array.isArray(domain.domainInfo?.categories) && domain.domainInfo.categories.length > 0
                    ? domain.domainInfo.categories
                    : []);

            const legacyFallback = domain.segmento || domain.domainInfo?.segmento || domain.category || domain.domainInfo?.category || '';
            const domainCategories = rawCategories
                .filter(Boolean)
                .map(item => (typeof item === 'string' ? item : item?.value || item?.label || ''))
                .filter(Boolean);

            if (domainCategories.length === 0 && legacyFallback) {
                domainCategories.push(legacyFallback);
            }

            setFormData({
                _id: domain._id,
                domain: domain.domain || '',
                siteName: domain.siteName || domain.domain || '',
                pais: domain.pais || 'BR',
                sede: domain.sede || '',
                estado: domain.estado || (initialUf ? ufMapping[initialUf] : ''),
                cidade: domain.cidade || '',
                uf: initialUf,
                tipo: domain.tipo || 'Site',
                abrangencia: domain.abrangencia || '',
                categories: domainCategories,
                audienceMonthly: audienceMonthly,
                audienceDaily: domain.audienceDaily || domain.audience || Math.round(audienceMonthly / (365 / 12)),
                ppv: ppv,
                subscribers: domain.subscribers || 0,
                pageViews: domain.pageViews || 0,
                pageViewsMonthly: domain.pageViewsMonthly || 0,
                description: domain.description || domain.notes || '',
                updateSource: domain.updateSource || 'automatic',
                // USAR VALORES DO BANCO - NÃO RECALCULAR
                cpc: domain.cpc || 0,
                valuation: domain.valuation || domain.estimatedValuation || 0,
                tier: domain.tier || 3
            });

            console.log('[DomainEditDialog] Using backend values - CPC:', domain.cpc, 'Valuation:', domain.valuation);
        }
    }, [domain, open]); // Atualizar quando abrir ou mudar o domínio

    const selectedCategoryOptions = useMemo(() => {
        const catalogMap = new Map();
        availableCategories.forEach(cat => {
            catalogMap.set(normalizeKey(cat.value), cat);
            catalogMap.set(normalizeKey(cat.label), cat);
        });

        return (formData.categories || [])
            .filter(Boolean)
            .map((value) => {
                const text = extractCategoryText(value);
                return catalogMap.get(normalizeKey(text)) || {
                value: text,
                label: text,
                description: 'Categoria legada'
                };
            })
            .filter(item => extractCategoryText(item));
    }, [availableCategories, formData.categories]);

    const handleSave = () => {
        // Incluir domainInfoId se disponível (para domínios com updateSource='manual')
        const dataToSave = {
            ...formData
        };

        // Se o domínio tem domainInfoId, incluir para redirecionar para DomainInfo API
        if (domain?.domainInfoId) {
            dataToSave.domainInfoId = domain.domainInfoId;
        }

        onSave(dataToSave);
    };

    return (
        <PlatformDialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <PlatformDialogTitle
                title={isNew ? 'Novo Domínio' : 'Editar Domínio'}
                subtitle={isNew ? 'Cadastre uma nova fonte no sistema.' : formData.domain}
                icon={<EditIcon />}
                onClose={onClose}
            />
            <DialogContent sx={{ pt: 3, pb: 2 }}>
                {formData.updateSource === 'manual' && (
                    <Alert
                        severity="warning"
                        variant="outlined"
                        sx={{ mb: 3, borderRadius: 2 }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    setFormData({ ...formData, updateSource: 'automatic' });
                                }}
                                sx={{ fontWeight: 700 }}
                            >
                                Reverter para Auto
                            </Button>
                        }
                    >
                        Este domínio foi editado manualmente e não receberá atualizações automáticas de audiência.
                    </Alert>
                )}

                <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Domínio Principal"
                            size="small"
                            value={formData.domain}
                            onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                            required
                            disabled={!isNew}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Nome de Exibição (Site)"
                            size="small"
                            value={formData.siteName}
                            onChange={(e) => setFormData({ ...formData, siteName: e.target.value })}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Tipo de Fonte</InputLabel>
                            <Select
                                value={formData.tipo}
                                onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                                label="Tipo de Fonte"
                                sx={{ borderRadius: 2 }}
                            >
                                {tipos.map(tipo => (
                                    <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>País de Origem</InputLabel>
                            <Select
                                value={formData.pais}
                                onChange={(e) => setFormData({ ...formData, pais: e.target.value })}
                                label="País de Origem"
                                sx={{ borderRadius: 2 }}
                            >
                                {paises.map(pais => (
                                    <MenuItem key={pais.code} value={pais.code}>
                                        {pais.name} ({pais.code})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Sede / Redação"
                            size="small"
                            value={formData.sede}
                            onChange={(e) => setFormData({ ...formData, sede: e.target.value })}
                            placeholder="ex: Londres, São Paulo"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        {formData.pais === 'BR' ? (
                            <Autocomplete
                                size="small"
                                options={Object.values(ufMapping)}
                                value={formData.estado || ''}
                                onChange={(event, newValue) => {
                                    const selectedUf = estadoToUf[newValue?.toLowerCase()] || '';
                                    setFormData({
                                        ...formData,
                                        estado: newValue || '',
                                        uf: selectedUf
                                    });
                                }}
                                onInputChange={(event, newInputValue) => {
                                    const selectedUf = estadoToUf[newInputValue?.toLowerCase()] || '';
                                    setFormData({
                                        ...formData,
                                        estado: newInputValue,
                                        uf: selectedUf
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Estado (UF)"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                )}
                                freeSolo
                            />
                        ) : (
                            <TextField
                                fullWidth
                                label="Estado / Província"
                                size="small"
                                value={formData.estado}
                                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Cidade"
                            size="small"
                            value={formData.cidade}
                            onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Abrangência</InputLabel>
                            <Select
                                value={formData.abrangencia}
                                onChange={(e) => setFormData({ ...formData, abrangencia: e.target.value })}
                                label="Abrangência"
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem value="">Selecione</MenuItem>
                                {abrangencias.map(abr => (
                                    <MenuItem key={abr} value={abr}>{abr}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 700 }}>
                            Categorias estratégicas
                        </Typography>
                        <Autocomplete
                            multiple
                            size="small"
                            loading={loadingCategories}
                            options={availableCategories}
                            value={selectedCategoryOptions}
                            getOptionLabel={(option) => extractCategoryText(option)}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            onChange={(event, newValue) => {
                                setFormData({
                                    ...formData,
                                    categories: newValue.map(item => extractCategoryText(item)).filter(Boolean)
                                });
                            }}
                            renderTags={(tagValue, getTagProps) =>
                                tagValue.map((option, index) => (
                                    <Box
                                        key={`${extractCategoryText(option)}-${index}`}
                                        {...getTagProps({ index })}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            mr: 0.75,
                                            mb: 0.5,
                                            px: 1,
                                            py: 0.35,
                                            borderRadius: 999,
                                            bgcolor: 'action.selected',
                                            fontSize: '0.78rem',
                                            fontWeight: 700
                                        }}
                                    >
                                        {extractCategoryText(option)}
                                    </Box>
                                ))
                            }
                            renderOption={(props, option) => (
                                <li {...props} key={option.value}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="body2" fontWeight={700}>
                                            {option.label}
                                        </Typography>
                                        {option.description && (
                                            <Typography variant="caption" color="text.secondary">
                                                {option.description}
                                            </Typography>
                                        )}
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Categorias / Segmentos"
                                    placeholder={loadingCategories ? 'Carregando catálogo...' : 'Selecione categorias oficiais'}
                                    helperText="Estas categorias alimentam o match de Tier e devem vir do catálogo oficial."
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            )}
                        />
                        {selectedCategoryOptions.length === 0 && formData.categories && formData.categories.length > 0 && (
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {formData.categories.map((cat, index) => (
                                    <Box
                                        key={`${extractCategoryText(cat)}-${index}`}
                                        sx={{
                                            px: 1.25,
                                            py: 0.5,
                                            borderRadius: 999,
                                            bgcolor: 'action.hover',
                                            fontSize: '0.75rem',
                                            fontWeight: 700
                                        }}
                                    >
                                        {extractCategoryText(cat)}
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {selectedCategoryOptions.length > 0 && (
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {selectedCategoryOptions.map((cat) => (
                                    <Box
                                        key={cat.value}
                                        sx={{
                                            px: 1.25,
                                            py: 0.5,
                                            borderRadius: 999,
                                            bgcolor: 'action.hover',
                                            fontSize: '0.75rem',
                                            fontWeight: 700
                                        }}
                                    >
                                        {cat.label}
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {domain?.enrichedAt && (
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main', fontSize: '0.75rem', fontWeight: 600 }}>
                                <IAIcon sx={{ fontSize: 14 }} />
                                Dados enriquecidos por IA em {new Date(domain.enrichedAt).toLocaleDateString('pt-BR')}
                            </Box>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>
                            Dados de Audiência e Performance
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Audiência Mensal (Visitas)"
                            size="small"
                            value={formatNumber(formData.audienceMonthly)}
                            onChange={(e) => {
                                const rawVal = e.target.value;
                                if (!/^[0-9.,]*$/.test(rawVal)) return;

                                const numVal = parseNumber(rawVal);
                                const derived = recalculateValues(numVal, formData.ppv);
                                setFormData({
                                    ...formData,
                                    audienceMonthly: numVal,
                                    ...derived,
                                    updateSource: 'manual'
                                });
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="PPV (Págs/Visitante)"
                            size="small"
                            value={ppvText}
                            onChange={(e) => {
                                const rawVal = e.target.value;
                                if (!/^[0-9]*[,.]?[0-9]*$/.test(rawVal)) return;
                                setPpvText(rawVal);
                            }}
                            onBlur={() => {
                                const numVal = parseNumber(ppvText, true) || 2.5;
                                const derived = recalculateValues(formData.audienceMonthly, numVal);
                                setPpvText(numVal.toString().replace('.', ','));
                                setFormData({
                                    ...formData,
                                    ppv: numVal,
                                    ...derived,
                                    updateSource: 'manual'
                                });
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Seguidores / Inscritos"
                            size="small"
                            value={formatNumber(formData.subscribers)}
                            onChange={(e) => {
                                const val = parseNumber(e.target.value);
                                setFormData({
                                    ...formData,
                                    subscribers: val,
                                    updateSource: 'manual'
                                });
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Audiência Diária"
                            size="small"
                            value={formatNumber(formData.audienceDaily)}
                            InputProps={{ readOnly: true }}
                            sx={{ 
                                bgcolor: 'action.hover',
                                '& .MuiOutlinedInput-root': { borderRadius: 2 }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Page Views Mensal"
                            size="small"
                            value={formatNumber(formData.pageViewsMonthly)}
                            InputProps={{ readOnly: true }}
                            sx={{ 
                                bgcolor: 'action.hover',
                                '& .MuiOutlinedInput-root': { borderRadius: 2 }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Valoração Estimada"
                            size="small"
                            value={`R$ ${formatNumber(formData.valuation)}`}
                            InputProps={{ readOnly: true }}
                            sx={{ 
                                bgcolor: 'action.hover',
                                '& .MuiOutlinedInput-root': { borderRadius: 2, fontWeight: 700, color: 'success.main' }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Tier Atual</InputLabel>
                            <Select
                                value={formData.tier}
                                onChange={(e) => setFormData({ ...formData, tier: parseInt(e.target.value) })}
                                label="Tier Atual"
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem value={1}>Tier 1 (Mega/Nacional)</MenuItem>
                                <MenuItem value={2}>Tier 2 (Regional/Médio)</MenuItem>
                                <MenuItem value={3}>Tier 3 (Local/Nicho)</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Notas e Descrição"
                            multiline
                            rows={3}
                            size="small"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
                <Button onClick={onClose} sx={{ fontWeight: 600 }}>
                    Cancelar
                </Button>
                <Button 
                    onClick={handleSave} 
                    variant="contained"
                    sx={{ fontWeight: 700, borderRadius: 2, px: 4 }}
                >
                    Salvar Alterações
                </Button>
            </DialogActions>
        </PlatformDialog>
    );
};

export default DomainEditDialog;
